import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { toast } from 'sonner';

const CollectionTracking = ({ language }) => {
  const [timeRange, setTimeRange] = useState('month');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalReminders: 0,
    emailsSent: 0,
    dashboardNotifications: 0,
    responseRate: 0,
    paymentConversion: 0,
    averageCollectionTime: 0,
    totalCollected: 0,
    outstandingAmount: 0
  });
  const [reminderHistory, setReminderHistory] = useState([]);
  const [phaseMetrics, setPhaseMetrics] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUserId) {
      loadCollectionData();
    }
  }, [currentUserId, timeRange]);

  const loadCurrentUser = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      setCurrentUserId(user?.id);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const loadCollectionData = async () => {
    try {
      setLoading(true);

      // Charger l'historique des rappels
      const { data: reminders, error: remindersError } = await supabase
        .from('payment_reminders')
        .select(`
          *,
          invoice:invoices(
            invoice_number,
            client_name,
            total_amount,
            status
          )
        `)
        .eq('user_id', currentUserId)
        .order('scheduled_date', { ascending: false })
        .limit(50);

      if (remindersError) throw remindersError;

      // Charger l'historique des notifications
      const { data: history, error: historyError } = await supabase
        .from('notification_history')
        .select('*')
        .eq('user_id', currentUserId)
        .order('sent_at', { ascending: false })
        .limit(100);

      if (historyError) throw historyError;

      // Calculer les statistiques
      const emailCount = history?.filter(h => h.channel === 'email').length || 0;
      const dashboardCount = history?.filter(h => h.channel === 'dashboard').length || 0;
      const totalSent = reminders?.filter(r => r.status === 'sent').length || 0;

      // Calculer le taux de réponse (factures payées après rappel)
      const paidAfterReminder = reminders?.filter(r => 
        r.invoice?.status === 'paid' && r.status === 'sent'
      ).length || 0;
      const responseRate = totalSent > 0 ? (paidAfterReminder / totalSent) * 100 : 0;

      // Charger les factures
      const { data: invoices } = await supabase
        .from('invoices')
        .select('*')
        .eq('client_id', currentUserId);

      const totalCollected = invoices?.filter(inv => inv.status === 'paid')
        .reduce((sum, inv) => sum + parseFloat(inv.amount_paid || 0), 0) || 0;

      const outstandingAmount = invoices?.filter(inv => inv.status !== 'paid')
        .reduce((sum, inv) => sum + parseFloat(inv.balance_due || 0), 0) || 0;

      setStats({
        totalReminders: totalSent,
        emailsSent: emailCount,
        dashboardNotifications: dashboardCount,
        responseRate: responseRate.toFixed(1),
        paymentConversion: (paidAfterReminder / (totalSent || 1) * 100).toFixed(1),
        averageCollectionTime: 12, // À calculer
        totalCollected: totalCollected,
        outstandingAmount: outstandingAmount
      });

      // Formater l'historique
      const formattedHistory = reminders?.slice(0, 10).map(reminder => ({
        id: reminder.id,
        date: reminder.scheduled_date,
        invoiceNumber: reminder.invoice?.invoice_number || reminder.invoice_number,
        clientName: reminder.invoice?.client_name || 'N/A',
        amount: parseFloat(reminder.amount),
        reminderType: reminder.channels_used?.[0] || 'email',
        escalationLevel: reminder.reminder_type === 'escalation' ? 3 : 
                         reminder.reminder_type === 'overdue' ? 2 : 1,
        status: reminder.status,
        opened: reminder.email_sent || reminder.dashboard_notified,
        clicked: false,
        paid: reminder.invoice?.status === 'paid'
      }));

      setReminderHistory(formattedHistory);

      // Charger les métriques par phase
      await loadPhaseMetrics();

    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadPhaseMetrics = async () => {
    try {
      // Récupérer les cas de l'utilisateur
      const { data: cases } = await supabase
        .from('cases')
        .select('id')
        .eq('user_profile_id', currentUserId);

      const caseIds = cases?.map(c => c.id) || [];

      if (caseIds.length === 0) {
        setPhaseMetrics([]);
        return;
      }

      // Récupérer toutes les phases
      const { data: phaseInvoices } = await supabase
        .from('case_phase_invoices')
        .select(`
          *,
          invoice:invoices(*)
        `)
        .in('case_id', caseIds);

      // Grouper par nom de phase
      const phaseGroups = {};
      phaseInvoices?.forEach(phase => {
        const phaseName = phase.phase_name;
        if (!phaseGroups[phaseName]) {
          phaseGroups[phaseName] = {
            phaseName: phaseName,
            totalInvoices: 0,
            overdueCount: 0,
            overdueAmount: 0,
            paidCount: 0
          };
        }

        phaseGroups[phaseName].totalInvoices++;

        if (phase.invoice) {
          const isOverdue = phase.invoice.due_date < new Date().toISOString().split('T')[0] &&
                           phase.invoice.status !== 'paid';
          
          if (isOverdue) {
            phaseGroups[phaseName].overdueCount++;
            phaseGroups[phaseName].overdueAmount += parseFloat(phase.invoice.balance_due || 0);
          }

          if (phase.invoice.status === 'paid') {
            phaseGroups[phaseName].paidCount++;
          }
        }
      });

      // Calculer le taux de collection
      const metrics = Object.values(phaseGroups).map(group => ({
        ...group,
        collectionRate: group.totalInvoices > 0 
          ? ((group.paidCount / group.totalInvoices) * 100).toFixed(1)
          : 0
      }));

      setPhaseMetrics(metrics);
    } catch (error) {
      console.error('Erreur lors du chargement des métriques:', error);
    }
  };

  const formatAmount = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getStatusBadge = (status) => {
    const configs = {
      sent: { label: 'Envoyé', className: 'bg-success/10 text-success', icon: 'CheckCircle' },
      pending: { label: 'En attente', className: 'bg-warning/10 text-warning', icon: 'Clock' },
      failed: { label: 'Échec', className: 'bg-error/10 text-error', icon: 'XCircle' }
    };
    return configs?.[status] || configs?.sent;
  };

  const handleExport = () => {
    toast.info('Export en cours de développement');
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-12">
        <div className="flex items-center justify-center">
          <Icon name="Loader" size={32} className="animate-spin text-primary mr-3" />
          <p className="text-sm text-muted-foreground">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Send" size={20} className="text-primary" />
            <p className="text-sm text-muted-foreground">Rappels envoyés</p>
          </div>
          <p className="text-2xl font-bold text-foreground">{stats.totalReminders}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {stats.emailsSent} emails, {stats.dashboardNotifications} dashboard
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="TrendingUp" size={20} className="text-success" />
            <p className="text-sm text-muted-foreground">Taux de réponse</p>
          </div>
          <p className="text-2xl font-bold text-foreground">{stats.responseRate}%</p>
          <p className="text-xs text-success mt-1">Performance actuelle</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="DollarSign" size={20} className="text-warning" />
            <p className="text-sm text-muted-foreground">Taux de conversion</p>
          </div>
          <p className="text-2xl font-bold text-foreground">{stats.paymentConversion}%</p>
          <p className="text-xs text-muted-foreground mt-1">{formatAmount(stats.totalCollected)} collectés</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Clock" size={20} className="text-primary" />
            <p className="text-sm text-muted-foreground">Délai moyen</p>
          </div>
          <p className="text-2xl font-bold text-foreground">{stats.averageCollectionTime} jours</p>
          <p className="text-xs text-muted-foreground mt-1">{formatAmount(stats.outstandingAmount)} en attente</p>
        </div>
      </div>

      {/* Phase-Based Metrics */}
      {phaseMetrics.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="Layers" size={24} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">
              Métriques par phase
            </h3>
          </div>
          <div className="space-y-4">
            {phaseMetrics.map((phase, index) => (
              <div key={index} className="bg-muted/30 border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-foreground">{phase.phaseName}</h4>
                  <span className="text-sm font-bold text-primary">{phase.collectionRate}%</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Total factures</p>
                    <p className="text-lg font-bold text-foreground">{phase.totalInvoices}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">En retard</p>
                    <p className="text-lg font-bold text-warning">{phase.overdueCount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Montant en retard</p>
                    <p className="text-lg font-bold text-error">{formatAmount(phase.overdueAmount)}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${phase.collectionRate}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reminder History */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Icon name="History" size={24} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">
              Historique des rappels
            </h3>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Icon name="Download" size={16} className="mr-2" />
            Exporter
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Facture</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Montant</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Niveau</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {reminderHistory.map((reminder) => {
                const statusConfig = getStatusBadge(reminder.status);
                return (
                  <tr key={reminder.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 text-sm text-foreground">{formatDate(reminder.date)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{reminder.invoiceNumber}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{reminder.clientName}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-foreground">{formatAmount(reminder.amount)}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                        {reminder.reminderType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{reminder.escalationLevel}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.className}`}>
                        <Icon name={statusConfig.icon} size={12} />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {reminder.opened && <Icon name="Eye" size={14} className="text-success" title="Ouvert" />}
                        {reminder.clicked && <Icon name="MousePointer" size={14} className="text-primary" title="Cliqué" />}
                        {reminder.paid && <Icon name="CheckCircle" size={14} className="text-success" title="Payé" />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {reminderHistory.length === 0 && (
          <div className="text-center py-8">
            <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Aucun historique de rappel</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionTracking;